import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  fetchTechPatients,
  editTechPatient,
  updateTechPatient,
  trashTechPatient,
} from "../../Adapters/ApiAdapter";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import ReactQuill from "react-quill";
import { baseImage_path, baseUrl } from "../../variables/Variables";
import CKEditorField from "../../components/CKEditorField";

const TechPatientsList = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editPatient, setEditPatient] = useState(null);
  const [newImage, setNewImage] = useState(null);
  const [deletePatientId, setDeletePatientId] = useState(null);

  useEffect(() => {
    const loadPatients = async () => {
      try {
        const response = await fetchTechPatients();
        if (response?.data) {
          setPatients((response.data).sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )
          );
        } else {
          setError("Failed to fetch tech patients.");
        }
      } catch (err) {
        setError("An error occurred while fetching tech patients.");
      } finally {
        setLoading(false);
      }
    };

    loadPatients();
  }, []);

  const handleEdit = async (id) => {
    try {
      const response = await editTechPatient(id);
      setEditPatient(response.data);
      setNewImage(null); // Reset new image
    } catch (err) {
      console.error("Error loading edit data:", err);
    }
  };

  const handleSaveEdit = async () => {
    const formData = new FormData();

    formData.append("name", editPatient.name);
    formData.append("content", editPatient.content);
    if (newImage) formData.append("blog_img", newImage);

    try {
      const response = await updateTechPatient(editPatient.id, formData);
      console.log("Edit response:", response);
      setEditPatient(null);

      // Show success message
      toast.success("Patient Technology details updated successfully!");

      // Refresh the list after update
      const refreshedData = await fetchTechPatients();
      setPatients(refreshedData.data);
    } catch (err) {
      console.error("Error saving edit:", err);
      toast.error("Failed to update patient details.");
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await trashTechPatient(id);
      console.log("Delete response:", response);
      setDeletePatientId(null);

      // Show success message
      toast.success("Patient technology deleted successfully!");

      // Refresh the list after deletion
      const refreshedData = await fetchTechPatients();
      setPatients(refreshedData.data);
    } catch (err) {
      console.error("Error deleting tech patient:", err);
      toast.error("Failed to delete patient.");
    }
  };

  const stripHtml = (html) => html.replace(/<[^>]+>/g, "").trim();

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "name", headerName: "Name", width: 200 },
    { field: "source_page", headerName: "Source Page", width: 150 },
    { field: "content", headerName: stripHtml("Content"), width: 300 },
    {
      field: "created_at",
      headerName: "Created At",
      width: 200,
      valueFormatter: (params) =>
        dayjs(params.value).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeletePatientId(params.row.id)}
            style={{ marginLeft: "8px" }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Tech Patients List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGrid rows={patients} columns={columns} autoHeight />
      )}

      {/* Edit Dialog */}
      {editPatient && (
        <Dialog open onClose={() => setEditPatient(null)}>
          <DialogTitle>Edit Patient</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              value={editPatient.name}
              onChange={(e) =>
                setEditPatient({ ...editPatient, name: e.target.value })
              }
            />

            <Typography variant="body1">Content</Typography>
            {/* <ReactQuill
              theme="snow"
              value={editPatient.content}
              onChange={(e) => setEditPatient({ ...editPatient, content: e })}
              style={{ height: "200px", marginBottom: "50px" }}
            /> */}
            <CKEditorField
            value={editPatient.content}
            onChange={(data) => setEditPatient((prev) => ({ ...prev, content: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=tech-patient`}
          />

            <Button variant="contained" component="label">
              Upload Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => setNewImage(e.target.files[0])}
              />
            </Button>
            {newImage && <Typography mt={1}>{newImage.name}</Typography>}
            {editPatient.blog_img && (
              <Box mt={2}>
                <Typography variant="subtitle1">Current Image:</Typography>
                <img
                  src={
                    typeof editPatient.blog_img === "string"
                      ? `${baseImage_path}/${editPatient.blog_img}`
                      : URL.createObjectURL(editPatient.blog_img)
                  }
                  alt="Blog"
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditPatient(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveEdit}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      {deletePatientId && (
        <Dialog open onClose={() => setDeletePatientId(null)}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this tech patient?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeletePatientId(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deletePatientId)}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default TechPatientsList;
